<template>
  <div>
    <PageAllow controller="DeliveryPoints" action="relations" />
    <Breadcrumbs :items="items" />

    <v-slide-x-transition>
      <v-sheet class="transparent-background-color">
        <v-row dense class="mx-5">
          <v-col cols="12" sm="12" md="4">
            <v-row dense align="center" class="pa-1">
              <SearcherKeyword
                ref="keyword"
                v-model="options.keyword"
                @text="keyword = $event"
                :label="$t('DeliveryPoint.relationKeyword', [$consts.shopName()])"
              />
            </v-row>
          </v-col>
          <v-col cols="12" sm="12" md="8" align-self="center">
            <v-row dense align="center" class="search-controls">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="px-0 py-0 caption font-weight-bold grey--text"
                    color="primary"
                    @click.stop="onSearch()"
                  >
                    <v-icon small class="pe-1">fa-search</v-icon>
                    {{ $t("Search.search") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.searchTip") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="caption font-weight-bold grey--text"
                    @click.stop="resetSearch()"
                  >
                    <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                    {{ $t("Search.reset") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.resetTip") }}</span>
              </v-tooltip>
              <v-divider vertical />
              <v-btn
                class="caption font-weight-bold grey--text"
                :class="
                  $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                "
                small
                text
                @click.stop="$store.commit('toggleSearchPanel')"
              >
                {{ $t("Search.pro") }}
                <v-icon small class="ps-1">
                  {{ $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down" }}
                </v-icon>
              </v-btn>
              <v-spacer />
            </v-row>
          </v-col>
        </v-row>

        <v-expansion-panels
          v-model="$store.getters.panels"
          multiple
          flat
          accordion
        >
          <v-expansion-panel class="transparent-background-color">
            <v-expansion-panel-content>
              <v-row dense align="center">
                <!-- 检索条件 -->
                <!-- 仓库 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherStocks v-model="stocksSelected" />
                </v-col>
                <!-- 门店 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherShops v-model="shopsSelected" />
                </v-col>
                <!-- 配送点 -->
                <v-col cols="12" sm="6" md="4" v-if="settings && settings.delivery_point && $permission.permissionAllow('DeliveryPoints', 'index')">
                  <SearcherDeliveryPoints v-model="deliveryPointsSelected" />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="mx-4 mb-4">
          <v-data-table
            :headers="headers"
            :items="deliveryPoints"
            :options.sync="options"
            :page.sync="paging.page"
            :server-items-length="paging.count"
            :loading="loading"
            multi-sort
            class="elevation-1 px-4 pt-1 sticky-table"
            id="sticky-table"
            fixed-header
            :footer-props="$t('$config.footer-props')"
          >
            <template v-slot:item.created="{ item }">
              {{ item.created | formatTime }}
            </template>
          </v-data-table>
        </div>
      </v-sheet>
    </v-slide-x-transition>
  </div>
</template>

<script>
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import i18n from "@/i18n";
import { mapState } from "vuex";
import DeliveryPoints from "@/api/delivery_points";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherStocks from "@/components/plugins/SearcherStocks";
import SearcherShops from "@/components/plugins/SearcherShops";
import SearcherDeliveryPoints from "@/components/plugins/SearcherDeliveryPoints";

const defaultOptions = {
  keyword: "",
  stock_ids: null,
  shop_ids: null,
  delivery_point_ids: null,
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherKeyword,
    SearcherStocks,
    SearcherShops,
    SearcherDeliveryPoints,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.stockManagement"),
        disabled: false,
        href: "/stocks",
      },
      {
        text: i18n.t("Nav.stocks"),
        disabled: false,
        href: "/stocks",
      },
      {
        text: i18n.t("DeliveryPoint.title"),
        disabled: false,
        href: "/stocks/delivery-point",
      },
      {
        text: i18n.t("DeliveryPoint.relationBtn"),
        disabled: true,
        href: "/stocks/delivery-point/relations",
      },
    ],

    headers() {
      return [
        {
          text: i18n.t("DeliveryPoint.RelationFields.stockCode"),
          align: "center",
          sortable: false,
          value: "stock.code",
        },
        {
          text: i18n.t("DeliveryPoint.RelationFields.stockName"),
          align: "center",
          sortable: false,
          value: "stock.name",
        },
        {
          text: i18n.t("DeliveryPoint.RelationFields.shopCode", [this.$consts.shopName()]),
          align: "center",
          sortable: false,
          value: "shop.shop_code",
        },
        {
          text: i18n.t("DeliveryPoint.RelationFields.shopName", [this.$consts.shopName()]),
          align: "center",
          sortable: false,
          value: "shop.shop_name",
        },
        {
          text: i18n.t("DeliveryPoint.RelationFields.pointCode"),
          align: "center",
          sortable: false,
          value: "delivery_point.code",
        },
        {
          text: i18n.t("DeliveryPoint.RelationFields.pointName"),
          align: "center",
          sortable: false,
          value: "delivery_point.name",
        },
        {
          text: i18n.t("DeliveryPoint.RelationFields.created"),
          align: "center",
          sortable: false,
          value: "created",
        },
      ]
    },

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings"]),
  },

  mounted() {
    this.$commonUtils.loadOptions("deliveryPointRelations", ["stocksSelected", "shopsSelected", "deliveryPointsSelected"]).then(
      function (data) {
        if (data.options) {
          this.options = data.options;
        } else this.resetSearch();
         data.selecteds.forEach((selected) => {
           switch (selected.key) {
             case "stocksSelected":
               this.stocksSelected = selected.value;
               break;
             case "shopsSelected":
               this.shopsSelected = selected.value;
               break;
             case "deliveryPointsSelected":
               this.deliveryPointsSelected = selected.value;
               break;
           }
         });
      }.bind(this)
    );
  },

  data: () => ({
    deliveryPoints: [],
    paging: {},
    options: JSON.parse(
      sessionStorage.getItem("deliveryPointRelations-options")
        ? sessionStorage.getItem("deliveryPointRelations-options")
        : JSON.stringify(defaultOptions)
    ),
    keyword: "",
    stocksSelected: null,
    shopsSelected: null,
    deliveryPointsSelected: null,
  }),

  methods: {
    resetSearch() {
      this.stocksSelected = [];
      this.shopsSelected = [];
      this.deliveryPointsSelected = [];
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.keyword].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        keyword: this.keyword,
      });
    },
    getDeliveryPointFromAPI() {
      DeliveryPoints.getRelations(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.deliveryPoints = response.data.delivery_point_relations;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
  },

  watch: {
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getDeliveryPointFromAPI();
        sessionStorage.setItem("deliveryPointRelations-options", JSON.stringify(this.options));
      },
    },
    stocksSelected: {
      handler() {
        this.options.stock_ids = this.stocksSelected
          ? this.stocksSelected.map((e) => {
              return e.id;
            })
          : null;
        sessionStorage.setItem(
          "deliveryPointRelations-stocksSelected",
          JSON.stringify(this.stocksSelected)
        );
      },
    },
    shopsSelected: {
      handler() {
        this.options.shop_ids = this.shopsSelected
          ? this.shopsSelected.map((e) => {
              return e.id;
            })
          : null;
        sessionStorage.setItem(
          "deliveryPointRelations-shopsSelected",
          JSON.stringify(this.shopsSelected)
        );
      },
    },
    deliveryPointsSelected: {
      handler() {
        this.options.delivery_point_ids = this.deliveryPointsSelected
          ? this.deliveryPointsSelected.map((e) => {
              return e.id;
            })
          : null;
        sessionStorage.setItem(
          "deliveryPointRelations-deliveryPointsSelected",
          JSON.stringify(this.deliveryPointsSelected)
        );
      },
    }
  },

  beforeDestroy() {
    this.$store.dispatch("threadLoadingStopAll");
  },
};
</script>